import { useEffect } from 'react';

import { useAppDispatch } from '@app/store/store';
import { useModal } from '@contexts/modal-context/ModalContext';
import { invokeInactivityLogout } from '@features/authentication/store/authentication.thunks';
import {
  ACTIVITY_THROTTLE_MS,
  INACTIVITY_LIMIT_MS,
  markActivity,
  readLastActivity,
  subscribeToActivity,
  WARNING_COUNTDOWN_MS,
} from '@features/authentication/utils/inactivity';

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;
const LOGOUT_AT_MS = INACTIVITY_LIMIT_MS + WARNING_COUNTDOWN_MS;

/**
 * THE CROSS-TAB INACTIVITY MONITOR
 * Watches for user inactivity and logs them out if they fall asleep at the keyboard.
 * * How it works:
 * - It uses `localStorage` as a shared "Clock". Any activity in ANY tab updates this clock.
 * - Every 1 second, it checks the clock.
 * - If idle time hits the Warning Limit -> Show the warning modal.
 * - If idle time hits the Absolute Limit -> Log them out.
 * * Note: This hook only runs while the user is authenticated (mounted in GlobalLayout).
 */

export const useInactivityMonitor = () => {
  const { openModal, closeModal, closeAllModals } = useModal();
  const dispatch = useAppDispatch();

  useEffect(() => {
    let lastWrite = 0;
    let loggedOut = false;
    let inactivityModalOpen = false;

    let localFallbackActivity = Date.now();

    // The single source of truth: how long has it been since anyone touched any tab?
    const evaluate = () => {
      const lastActivity = readLastActivity() ?? localFallbackActivity;
      const idleMs = Date.now() - lastActivity;

      // ZONE 3: DANGER (Time's up, log them out)
      if (idleMs >= LOGOUT_AT_MS) {
        if (!loggedOut) {
          loggedOut = true;
          // Hide all modals instantly so the user doesn't see weird UI glitches during teardown.
          closeAllModals();
          void dispatch(invokeInactivityLogout());
        }
        return;
      }

      // ZONE 2: WARNING (Show the countdown modal)
      if (idleMs >= INACTIVITY_LIMIT_MS) {
        if (!inactivityModalOpen) {
          inactivityModalOpen = true;
          openModal('INACTIVITY_LOGOUT');
        }
      }
      // ZONE 1: NORMAL (Hide the modal if they just became active again)
      else if (inactivityModalOpen) {
        inactivityModalOpen = false;
        closeModal('INACTIVITY_LOGOUT');
      }
    };

    // When the user moves the mouse or types in THIS tab:
    // We throttle this so we don't spam localStorage 60 times a second on mousemove.
    const handleLocalActivity = () => {
      const now = Date.now();
      if (now - lastWrite <= ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastWrite = now;
      localFallbackActivity = now;
      markActivity(now);
      evaluate();
    };

    // When another tab sees activity, the shared clock changes.
    // We just re-evaluate our local state (we DO NOT write to localStorage again, to avoid infinite loops).
    const unsubscribe = subscribeToActivity(evaluate);

    // Browsers often "freeze" inactive tabs to save RAM/Battery, pausing their 1-second interval.
    // When the user switches back to this tab, we force an immediate re-evaluation
    // so they don't look at a stale clock.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        evaluate();
      }
    };

    ACTIVITY_EVENTS.forEach((event) => globalThis.addEventListener(event, handleLocalActivity));
    document.addEventListener('visibilitychange', handleVisibility);

    // Bootstrap: Count opening the app as "activity", then start the 1-second heartbeat.
    markActivity();
    evaluate();
    const interval = setInterval(evaluate, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => globalThis.removeEventListener(event, handleLocalActivity));
      document.removeEventListener('visibilitychange', handleVisibility);
      unsubscribe();
      clearInterval(interval);
      inactivityModalOpen = false;
      closeModal('INACTIVITY_LOGOUT');
    };
  }, [openModal, closeModal, closeAllModals, dispatch]);
};
